import React, { useEffect, useMemo, useState } from 'react';
import { demeterArtificialIntelligenceApi, demeterNewsApi } from '../../../Apis/Apis';
import {
    DemeterCommodity,
    DemeterNewsArticleModel,
    DemeterNewsArticleRatingModel,
    DemeterNewsArticleStatus,
    FundamentalMovement,
    ListDemeterArtificialIntelligenceModelsResponse,
    ListDemeterArtificialIntelligencePromptsResponse,
    ListDemeterNewsArticleRatingsResponse,
    ListDemeterNewsArticlesResponse,
    RatingSource,
    SortDirection,
} from '../../../Generated/Raven-Demeter';
import useApi from '../../Apis/Hooks/useApiHook';
import useMultipleApis from '../../Apis/Hooks/useMultipleApisHook';
import CardSkeleton from '../../Components/Layout/CardSkeleton';
import useLanguage from '../../Services/Language/useLanguageHook';
import { parseFieldConfidences } from './articleActions';
import ArticleDetail from './ArticleDetail';
import CommoditySidebar from './components/CommoditySidebar';
import NewsColumn from './components/NewsColumn';
import styles from './components/NewsComponents.module.scss';
import { ArticleWithRating } from './components/NewsDefinitions';

const NewsTab: React.FC = () => {
    const [translations] = useLanguage();
    const [selectedCommodity, setSelectedCommodity] = useState<DemeterCommodity | null>(null);
    const [selectedArticle, setSelectedArticle] = useState<DemeterNewsArticleModel | null>(null);

    const [promptsLoading, , promptsResponse] = useApi<void, ListDemeterArtificialIntelligencePromptsResponse>(
        () => demeterArtificialIntelligenceApi.listPrompts(),
        { errorMessage: 'Failed to load news feed', showResponseErrorMessage: true },
    );

    const [articlesLoading, , articlesResponse] = useApi<void, ListDemeterNewsArticlesResponse>(
        () =>
            demeterNewsApi.listDemeterNewsArticles(
                undefined,
                undefined,
                undefined,
                [DemeterNewsArticleStatus.Published],
                'ArticleDate',
                SortDirection.Desc,
                30,
                0,
            ),
        { errorMessage: 'Failed to load news feed', showResponseErrorMessage: true },
    );

    const [modelsLoading, , modelsResponse] = useApi<void, ListDemeterArtificialIntelligenceModelsResponse>(
        () => demeterArtificialIntelligenceApi.listModels(),
        { errorMessage: 'Failed to load news feed', showResponseErrorMessage: true },
    );

    const confidenceThreshold = useMemo(() => {
        const modelRows = modelsResponse?.rows ?? [];
        const fullRatingModel = modelRows.find((m) => m.promptType === 'FullRating');
        if (fullRatingModel?.parameters) {
            try {
                const params = JSON.parse(fullRatingModel.parameters);
                if (params.ConfidenceThreshold) {
                    return Number(params.ConfidenceThreshold);
                }
            } catch {
                // keep default
            }
        }
        return 0.5;
    }, [modelsResponse]);

    const promptGuid = useMemo(() => {
        const promptRows = promptsResponse?.rows ?? [];
        const sortedPrompts = [...promptRows].sort((a, b) => {
            const aTime = a.createdTime ? new Date(a.createdTime).getTime() : 0;
            const bTime = b.createdTime ? new Date(b.createdTime).getTime() : 0;
            return bTime - aTime;
        });
        return sortedPrompts[0]?.demeterArtificialIntelligencePromptGuid ?? null;
    }, [promptsResponse]);

    const articles = useMemo(() => (articlesResponse?.rows ?? []) as DemeterNewsArticleModel[], [articlesResponse]);

    const [ratingsLoading, refreshRatings, ratingsResponses] = useMultipleApis<void, ListDemeterNewsArticleRatingsResponse>(
        () => {
            if (articles.length === 0 || !promptGuid) {
                return null;
            }
            return articles.map((a) => demeterNewsApi.listDemeterNewsArticleRatings(a.demeterNewsArticleGuid, promptGuid));
        },
        { stopAutoExecute: true },
    );

    useEffect(() => {
        if (articles.length > 0 && promptGuid) {
            refreshRatings();
        }
    }, [articles, promptGuid]);

    const allArticleRatings = useMemo(() => {
        const ratingsMap = new Map<string, DemeterNewsArticleRatingModel[]>();
        if (ratingsResponses) {
            ratingsResponses.forEach((response, index) => {
                const rows = (response?.rows ?? []) as DemeterNewsArticleRatingModel[];
                const aiRatings = rows.filter((r) => r.ratingSource === RatingSource.ArtificialIntelligence);
                ratingsMap.set(articles[index].demeterNewsArticleGuid, aiRatings);
            });
        }
        return ratingsMap;
    }, [ratingsResponses, articles]);

    const loading = promptsLoading || articlesLoading || modelsLoading || ratingsLoading;

    const allPairs: ArticleWithRating[] = useMemo(() => {
        const pairs: ArticleWithRating[] = [];
        articles.forEach((article) => {
            const ratings = allArticleRatings.get(article.demeterNewsArticleGuid) ?? [];
            ratings.forEach((rating) => {
                pairs.push({ article, rating });
            });
        });
        return pairs;
    }, [articles, allArticleRatings]);

    const filteredPairs = useMemo(() => {
        if (!selectedCommodity) {
            return allPairs;
        }
        return allPairs.filter((pair) => pair.rating.commodity === selectedCommodity);
    }, [allPairs, selectedCommodity]);

    const getFieldConfidence = (rating: DemeterNewsArticleRatingModel, field: 'supply' | 'demand' | 'sentiment'): number => {
        const fc = parseFieldConfidences(rating.comments);
        return fc[field] ?? rating.confidenceScore ?? 0;
    };

    const filterByField = (field: 'supply' | 'demand' | 'sentiment', getFundamental: (r: DemeterNewsArticleRatingModel) => FundamentalMovement | undefined) =>
        filteredPairs.filter((p) => {
            const value = getFundamental(p.rating);
            return value && value !== FundamentalMovement.NotApplicable && getFieldConfidence(p.rating, field) >= confidenceThreshold;
        });

    const supplyPairs = useMemo(() => filterByField('supply', (r) => r.fundamentalSupply), [filteredPairs, confidenceThreshold]);
    const demandPairs = useMemo(() => filterByField('demand', (r) => r.fundamentalDemand), [filteredPairs, confidenceThreshold]);
    const sentimentPairs = useMemo(() => filterByField('sentiment', (r) => r.fundamentalSentiment), [filteredPairs, confidenceThreshold]);

    const availableCommodities: DemeterCommodity[] = useMemo(() => {
        const commodities = new Set<DemeterCommodity>();
        allPairs.forEach((p) => {
            const value = p.rating.commodity;
            if (!value) {
                return;
            }
            const hasQualifyingField =
                (p.rating.fundamentalSupply &&
                    p.rating.fundamentalSupply !== FundamentalMovement.NotApplicable &&
                    getFieldConfidence(p.rating, 'supply') >= confidenceThreshold) ||
                (p.rating.fundamentalDemand &&
                    p.rating.fundamentalDemand !== FundamentalMovement.NotApplicable &&
                    getFieldConfidence(p.rating, 'demand') >= confidenceThreshold) ||
                (p.rating.fundamentalSentiment &&
                    p.rating.fundamentalSentiment !== FundamentalMovement.NotApplicable &&
                    getFieldConfidence(p.rating, 'sentiment') >= confidenceThreshold);
            if (hasQualifyingField) {
                commodities.add(value as DemeterCommodity);
            }
        });
        return Array.from(commodities).sort();
    }, [allPairs, confidenceThreshold]);

    if (selectedArticle) {
        return <ArticleDetail article={selectedArticle} onBack={() => setSelectedArticle(null)} />;
    }

    if (loading) {
        return (
            <div className={styles.loading_wrapper}>
                <CardSkeleton minimumHeight={400} />
            </div>
        );
    }

    return (
        <div className={styles.news_layout} data-testid="AiNewsRatingNews">
            <CommoditySidebar commodities={availableCommodities} selectedCommodity={selectedCommodity} onSelect={setSelectedCommodity} />
            <div className={styles.columns_area}>
                <NewsColumn
                    title={translations.words.supply}
                    pairs={supplyPairs}
                    getMovement={(p) => p.rating.fundamentalSupply}
                    confidenceField="supply"
                    onSelectArticle={setSelectedArticle}
                />
                <NewsColumn
                    title={translations.words.demand}
                    pairs={demandPairs}
                    getMovement={(p) => p.rating.fundamentalDemand}
                    confidenceField="demand"
                    onSelectArticle={setSelectedArticle}
                />
                <NewsColumn
                    title={translations.aiNewsRating.priceSentiment}
                    pairs={sentimentPairs}
                    getMovement={(p) => p.rating.fundamentalSentiment}
                    confidenceField="sentiment"
                    onSelectArticle={setSelectedArticle}
                />
            </div>
        </div>
    );
};

export default NewsTab;
